import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"

import { useTranslation } from "react-i18next"
import type { PagingOptions } from "../../../../../models"
import { changeLinkChapterPage, deleteLinkChapter, fetchLinkChapterList, history } from "../../../../../redux"
import type { AppState, AppThunkDispatch, LinkChapter, LinkChapterActions } from "../../../../../redux"
import { CreateButton } from "../../../../atoms"
import { CoMaPagingBar, CrudDeleteDialog, CrudTableHeader, PageContainer } from "../../../../molecules"

export interface ILinkChapterManagerOverviewProps {
  chapters: LinkChapter[]
  pagingOptions: PagingOptions
  fetchChapters: () => void
  deleteChapter: (id: string) => void
  changePage: (page: number) => void
}

function LinkChapterManagerOverviewPageView(props: ILinkChapterManagerOverviewProps): ReactElement {
  const { chapters, pagingOptions, fetchChapters, deleteChapter, changePage } = props

  const { t } = useTranslation("contentManager")

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [idToRemove, setIdToRemove] = useState<string | undefined>(undefined)

  useEffect(() => {
    fetchChapters()
  }, [fetchChapters])

  function onCreateButtonClicked() {
    history.push("/manage/linkchapters/create")
  }

  function onEditButtonClicked(id: string) {
    history.push(`/manage/linkchapters/${id}`)
  }

  function onPageChange(page: number): void {
    if (page < 0 || page >= pagingOptions.totalPages || page === pagingOptions.currentPage) {
      return
    }
    changePage(page)
  }

  function onDeleteButtonClicked(id: string) {
    setDialogOpen(true)
    setIdToRemove(id)
  }

  function onDialogClose(): void {
    setDialogOpen(false)
    setIdToRemove(undefined)
  }

  function onDeleteConfirm(): void {
    if (idToRemove) deleteChapter(idToRemove)
    onDialogClose()
  }

  return (
    <PageContainer id="ChapterManagerOverview" noPadding>
      <CreateButton onClick={() => onCreateButtonClicked()} />
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <TableContainer>
        <Table>
          <CrudTableHeader headers={[{ key: "name", label: t("linkChapters.table.name") }]} onChange={fetchChapters} />
          <TableBody>
            {chapters.map((chapter: LinkChapter, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <TableRow key={index}>
                <TableCell>{chapter.name}</TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onEditButtonClicked(chapter.id as string)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onDeleteButtonClicked(chapter.id as string)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <CrudDeleteDialog open={dialogOpen} onClose={onDialogClose} possibleCascade onConfirm={onDeleteConfirm} />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    chapters: state.linkChapters.linkChapters,
    pagingOptions: state.linkChapters.pagingOptions
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<LinkChapterActions>) {
  return {
    fetchChapters: () => dispatch(fetchLinkChapterList()),
    deleteChapter: (id: string) => dispatch(deleteLinkChapter(id)),
    changePage: (page: number) => dispatch(changeLinkChapterPage(page))
  }
}

export const LinkChapterManagerOverviewPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(LinkChapterManagerOverviewPageView)
